/**
 * The executive look of the in-app GCC design dashboard and GCC TN Analysis page
 * (WI 2487), from the same sources as the GCC deliverables: the tokens in
 * GCC_EXECUTIVE_TOKENS and the brand palette from gccExecutivePalette (@tn/shared).
 *
 * gccExecutiveVars() returns them as CSS custom properties for the page container
 * (class gcc-exec), so they never reach :root and the rest of the app keeps its
 * look. gcc-executive.css only reads these variables.
 */
import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import type { ClientBranding } from '@shared/types'
import { GCC_EXECUTIVE_TOKENS, gccExecutivePalette } from '@tn/shared'
import './gcc-executive.css'

/**
 * Caption colour for text on the light surfaces. The shared --muted (#8A8EA6) is
 * about 3.2:1 on white, below the 4.5:1 the page needs for text, so it is kept
 * for lines and swatches and captions use this instead (about 5.6:1).
 */
export const GCC_MUTED_TEXT = '#62667F'

/** The token declarations as name → value pairs. */
const TOKEN_VARS: Record<string, string> = Object.fromEntries(
  GCC_EXECUTIVE_TOKENS.split(';')
    .map(d => d.trim())
    .filter(d => d.startsWith('--'))
    .map(d => [d.slice(0, d.indexOf(':')).trim(), d.slice(d.indexOf(':') + 1).trim()]),
)

/** The tokens and the brand palette as inline custom properties for the page container. */
export function gccExecutiveVars(branding?: Partial<Pick<ClientBranding, 'primaryColor' | 'accentColor'>>): CSSProperties {
  return {
    ...TOKEN_VARS,
    ...gccExecutivePalette(branding?.primaryColor, branding?.accentColor),
    '--muted-text': GCC_MUTED_TEXT,
  } as CSSProperties
}

/** A tinted badge: the exported dashboard's badge colours (b-green, b-blue, …). */
export interface GccBadgeStyle { bg: string; text: string }

export const GCC_BADGE = {
  green:  { bg: '#E3F6EC', text: '#0F6B45' },
  blue:   { bg: '#E6EEFF', text: '#2A3BA8' },
  purple: { bg: '#F3E6FF', text: '#6A00A8' },
  yellow: { bg: '#FFF4D6', text: '#8A5A00' },
  orange: { bg: '#FDEBDD', text: '#9A4A12' },
  red:    { bg: '#FDE7E5', text: '#A3211A' },
  gray:   { bg: '#EEF0F5', text: '#4A4E66' },
  cyan:   { bg: '#E1F3FB', text: '#0B6690' },
} as const satisfies Record<string, GccBadgeStyle>

/** Linked TN analysis scenarios: red to green by how much work leaves, Retained as the Retained badge. */
export const GCC_SCENARIO_BADGE: Record<string, GccBadgeStyle> = {
  High: GCC_BADGE.red, Medium: GCC_BADGE.orange, Low: GCC_BADGE.green, Retained: GCC_BADGE.blue,
}

/**
 * True when the page may animate: the browser can tell, and the user has not asked
 * for reduced motion. Without matchMedia (tests, old browsers) nothing animates.
 */
export function gccMotionAllowed(): boolean {
  return typeof window.matchMedia === 'function'
    && typeof window.requestAnimationFrame === 'function'
    && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Counts a figure up from 0 when motion is allowed; otherwise the final value at once. */
export function useCountUp(target: number, motion: boolean, durationMs = 900): number {
  const [value, setValue] = useState(motion ? 0 : target)
  useEffect(() => {
    if (!motion) return
    let frame = 0
    let start: number | null = null
    const tick = (now: number) => {
      start ??= now
      const t = Math.min(1, (now - start) / durationMs)
      setValue(t >= 1 ? target : target * (1 - Math.pow(1 - t, 3)))
      if (t < 1) frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [target, motion, durationMs])
  return motion ? value : target
}

/** The brand gradient of the hero and the disposition bars (WI 2487). */
export const BRAND_GRADIENT = 'linear-gradient(90deg, var(--brand), var(--brand2))'
